import { formatIDR } from "@/lib/api";
import { MonthNav } from "./MonthNav";

/* ---------- Summary Tab (Breakdown per Kategori) ---------- */
export function SummaryTab({ summary, month, setMonth, prevMonthLabel, shiftMonth, cashPlaza = { amount: 0, count: 0 }, payMix = {} }) {
  if (!summary) return <div className="text-zinc-400 text-sm">Memuat…</div>;

  // === Sumber Data Pemasukan (Hybrid) ===
  // 1. CASH (Penjualan Tunai) → dari cash_transactions (breakdown_in).
  //    Akun yang dikenali: 301 (generic tunai), 301-CP (Cash Plaza), 301-CK (Cash Kastem).
  //    Setelah migrasi 101→301 selesai, semua penjualan tunai akan masuk ke sini secara otomatis.
  // 2. SHOPEE → dari cash_transactions (breakdown_in). Sudah split per branch (301-SPP / 301-SPK).
  // 3. TRANSFER (BCA, Mandiri) → dari payMix (sales.payments[]).
  //    Alasan: info branch (plaza/kastem) untuk transfer HANYA ada di data sales,
  //    karena cash_transactions hanya menyimpan 301-BCA / 301-MDR tanpa suffix branch.

  // Shopee + Cash dari cash_transactions
  const CASH_AND_SHOPEE_CODES = ["301-SPP", "301-SPK", "301"];
  const byCode = Object.fromEntries((summary.breakdown_in || []).map((r) => [r.account_code, r]));
  const inMain = CASH_AND_SHOPEE_CODES.map((code) => byCode[code]).filter(Boolean);

  // Transfer BCA & Mandiri (per branch) dari payMix (data sales)
  const TRANSFER_ROWS = [
    { key: "bca_plaza",     code: "301-BCA-P", label: "Transfer BCA Plaza" },
    { key: "bca_kastem",    code: "301-BCA-K", label: "Transfer BCA Kastem" },
    { key: "mandiri_plaza", code: "301-MDR-P", label: "Transfer Mandiri Plaza" },
    { key: "mandiri_kastem",code: "301-MDR-K", label: "Transfer Mandiri Kastem" },
  ];
  for (const r of TRANSFER_ROWS) {
    const b = payMix[r.key];
    if (b && Number(b.amount || 0) > 0) {
      inMain.push({ account_code: r.code, account_name: r.label, amount: Number(b.amount), count: Number(b.count || 0) });
    }
  }
  const totalInAdjusted = inMain.reduce((s, r) => s + Number(r.amount || 0), 0);
  const maxIn = Math.max(...inMain.map((r) => r.amount), 1);
  const maxOut = Math.max(...(summary.breakdown_out || []).map((r) => r.amount), 1);
  return (
    <div>
      {/* StatCard Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200 mb-5">
        <div className="bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Pemasukan</div>
          <div data-testid="kasbon-total-open" className="font-mono text-2xl font-bold mt-1 text-[#008A00]">{formatIDR(totalInAdjusted)}</div>
        </div>
        <div className="bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Pengeluaran</div>
          <div data-testid="kasbon-total-settled" className="font-mono text-2xl font-bold mt-1 text-[#E81123]">{formatIDR(summary.total_out)}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <MonthNav value={month} onChange={setMonth} testIdPrefix="summary-month" prevMonthLabel={prevMonthLabel} shiftMonth={shiftMonth} />
        <div className="text-xs text-zinc-500 font-mono ml-2">Periode: {summary.period_start} s/d {summary.period_end}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <BreakdownCard
          title="Pemasukan"
          color="#008A00"
          rows={inMain}
          total={totalInAdjusted}
          max={maxIn}
        />
        <BreakdownCard title="Pengeluaran" color="#E81123" rows={summary.breakdown_out || []} total={summary.total_out} max={maxOut} />
      </div>
      <div className="mt-6 bg-zinc-900 text-white p-6 font-mono grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryStat label="Saldo Awal (Buku Kas)" value={summary.opening_balance} />
        <SummaryStat label="Pemasukan" value={totalInAdjusted} positive />
        <SummaryStat label="Pengeluaran" value={summary.total_out} negative />
        <SummaryStat label="Saldo Akhir (Jurnal Akuntansi)" value={totalInAdjusted - summary.total_out} big />
      </div>
    </div>
  );
}

function BreakdownCard({ title, color, rows, total, max, standaloneRows = [], standaloneNote = "" }) {
  return (
    <div className="border border-zinc-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-200">
        <div className="text-xs uppercase tracking-widest font-bold text-zinc-700">{title}</div>
        <div data-testid={`breakdown-total-${title.toLowerCase()}`} className="font-mono font-bold text-lg" style={{ color }}>{formatIDR(total)}</div>
      </div>
      {rows.length === 0 && <div className="text-zinc-400 text-xs font-mono py-6 text-center">Belum ada transaksi.</div>}
      <div className="space-y-2">
        {rows.map((r) => {
          const pct = (r.amount / max) * 100;
          return (
            <div key={r.account_code} data-testid="breakdown-row">
              <div className="flex justify-between text-xs mb-1">
                <span><span className="font-mono text-zinc-500 mr-2">{r.account_code}</span>{r.account_name}</span>
                <span className="font-mono font-bold text-zinc-900">{formatIDR(r.amount)}</span>
              </div>
              <div className="h-1.5 bg-zinc-100">
                <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color, opacity: 0.85 }} />
              </div>
              <div className="text-[10px] text-zinc-400 font-mono mt-0.5">{r.count} transaksi</div>
            </div>
          );
        })}
      </div>

      {standaloneRows.length > 0 && (
        <div className="mt-4 pt-3 border-t border-dashed border-zinc-300">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Di Luar Total</div>
          {standaloneNote && (
            <div className="text-[10px] font-mono text-zinc-500 mb-2 leading-relaxed">{standaloneNote}</div>
          )}
          <div className="space-y-2">
            {standaloneRows.map((r) => (
              <div key={r.account_code} data-testid="breakdown-standalone-row" className="flex justify-between text-xs items-baseline">
                <span><span className="font-mono text-zinc-500 mr-2">{r.account_code}</span>{r.account_name}</span>
                <span className="font-mono font-bold text-zinc-700">{formatIDR(r.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, positive, negative, big }) {
  const color = negative ? "text-[#ff9d9d]" : positive ? "text-[#4ade80]" : "text-white";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">{label}</div>
      <div className={`${big ? "text-2xl" : "text-lg"} font-bold mt-1 ${color}`}>{formatIDR(value)}</div>
    </div>
  );
}